import React, { useEffect, useState, useCallback } from "react";
import { useFormik, FieldArray } from "formik";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import AdMaxHeader from "./header";
import { updateBannerAds, updateUserProfile } from "../../../store/slices/authSlice";
import CropperModal from "../../../components/models/CropperModal";
import AdsUnpaid from "./AdsUnpaid";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Swal from "sweetalert2";

const validationSchema = Yup.object({
    bannerDisplayTime: Yup.number()
        .required("Display time is required")
        .min(1, "Time must be 1 sec or more"),
    banners: Yup.array().of(
        Yup.object().shape({
            link: Yup.string().url("Enter a valid URL"),
            bannerIdentifier: Yup.string().required("Banner Identifier is required"),
        })
    ),
});

const BannerAd = () => {
    const { user } = useSelector((state) => state.auth);
    const { attributes: { ads, enableAds } } = user;
    const dispatch = useDispatch();
    const [deletedBanners, setDeletedBanners] = useState([]);
    const [image, setImage] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [currentBannerIndex, setCurrentBannerIndex] = useState(null);

    const formik = useFormik({
        initialValues: {
            bannerDisplayTime: ads?.bannerDisplayTime || 0,
            banners: Array.isArray(ads?.banners) ? ads.banners : [],
        },
        validationSchema,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: async (values) => {
            const banners = Array.isArray(values.banners) ? values.banners : [];
            const filteredBanners = banners.filter((_, index) => !deletedBanners.includes(index));
            
            const updatedValues = {
                ...values,
                banners: filteredBanners,
            };
            const isForStation = user?.attributes?.isOwner == false ? true : false;
            dispatch(updateBannerAds({ values: { ads: updatedValues }, message: 'Banner Ad updated successfully',isForStation }));
            setDeletedBanners([]);
        },
    });

    useEffect(() => {
        const serverBanners = user?.attributes?.ads?.banners;
        // Only sync when the API returns a real array — never overwrite with undefined/null
        if (!Array.isArray(serverBanners)) return;
        const currentBanners = Array.isArray(formik.values.banners) ? formik.values.banners : [];
        if (serverBanners.length !== currentBanners.length) {
            formik.setFieldValue("banners", serverBanners);
        }
    }, [user.attributes?.ads?.banners]);

    const handleFileSelect = useCallback((file, index = null) => {
        if (file) {
            setCurrentBannerIndex(index);
            const reader = new FileReader();
            reader.onload = () => {
                setImage(reader.result);
                setShowModal(true);
            };
            reader.readAsDataURL(file);
        }
    }, []);

    const handleFileChange = (e, index = null) => {
        const file = e.target.files[0];
        handleFileSelect(file, index);
    };

    const triggerFileInput = (index = null) => {
        const inputId = index !== null ? `fileInput-${index}` : "fileInput";
        document.getElementById(inputId).click();
    };

    const showAlert = () => {
        Swal.fire("Not-Allowed", "Banner Ads is a premium feature that can be used in your application.<br> Please contact us for pricing: support@radiofxinc.com.");
    }


    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFileSelect(files[0]);
        }
    }, [handleFileSelect]);

    const handleCrop = (croppedImg) => {
        console.log(croppedImg,'croppedImg')
        setCroppedImage(croppedImg);
        const existingBanners = Array.isArray(formik.values.banners) ? formik.values.banners : [];
        if (currentBannerIndex !== null) {
            // Update specific banner image
            const updatedBanners = [...existingBanners];
            updatedBanners[currentBannerIndex] = {
                ...updatedBanners[currentBannerIndex],
                image: croppedImg,
            };
            formik.setFieldValue("banners", updatedBanners);
        } else {
            const bannerIdentifier = `Banner_${Date.now()}`;

            // Add new banner
            formik.setFieldValue("banners", [
                ...existingBanners,
                { link: "", bannerIdentifier, image: croppedImg }
            ]);
        }
        setShowModal(false);
        setCurrentBannerIndex(null);
    };

    const handleDelete = (index) => {
        setDeletedBanners([...deletedBanners, index]);
    };

    const handleUndoDelete = (index) => {
        setDeletedBanners(deletedBanners.filter((i) => i !== index));
    };

    const onDragEnd = (result) => {
        if (!result.destination) {
            return;
        }

        const items = Array.from(Array.isArray(formik.values.banners) ? formik.values.banners : []);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        formik.setFieldValue("banners", items);
    };

    return (
        <div className="section_main_div">
            <h1 className="section_heading">AdMax</h1>
            <AdMaxHeader />
            {!enableAds && <AdsUnpaid title={'Banner Ads'} />}
            <div className="InnerHeading">
                <h4  >Add Banner</h4>
                <h5 className="pb-2">1242*180 px. PNG format</h5>
            </div>
            <form className="" onSubmit={formik.handleSubmit}>
                <div className="custom-form-group mb-4" style={{ width: "560px" }}>

                    <label htmlFor="bannerDisplayTime">Banner display time (seconds)</label>
                    <input
                        type="number"
                        id="bannerDisplayTime"
                        name="bannerDisplayTime"
                        value={formik.values.bannerDisplayTime}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        required
                    />
                    {formik.touched.bannerDisplayTime && formik.errors.bannerDisplayTime ? (
                        <div className="error-text">{formik.errors.bannerDisplayTime}</div>
                    ) : null}
                    <hr className={formik.touched.bannerDisplayTime && formik.errors.bannerDisplayTime ? "error-hr-line" : ""} />
                </div>

                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="banners">
                        {(provided) => (
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                className="row w-100"
                            >
                                {formik.values.banners?.map((banner, index) => (
                                    <Draggable key={index} draggableId={`banner-${index}`} index={index}>
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                style={{
                                                    ...provided.draggableProps.style,
                                                    opacity: snapshot.isDragging ? 0.8 : 1,
                                                }}
                                                className="col-md-6"
                                            >
                                                <div className="w-100">
                                                    <div className="form-card" style={{ position: "relative" }}  {...provided.dragHandleProps}>
                                                        <div className="form-elements" style={{ position: "relative", zIndex: 2 }}>
                                                            <div className="d-flex">
                                                                <img
                                                                    src={banner.image || croppedImage || "default-image.png"}
                                                                    alt="Banner Preview"
                                                                    style={{ width: "414px", height: "auto" }}
                                                                />
                                                                {!deletedBanners.includes(index) && (
                                                                    <button
                                                                        style={{ border: "0px", outline: "0px", cursor: "pointer", background: "none", marginLeft: "10px" }}
                                                                        onClick={() => handleDelete(index)}
                                                                        type="button"
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <div className="custom-form-group">
                                                                <label htmlFor={`banners.${index}.bannerIdentifier`}>Banner Identifier</label>
                                                                <input
                                                                    type="text"
                                                                    id={`banners.${index}.bannerIdentifier`}
                                                                    {...formik.getFieldProps(`banners.${index}.bannerIdentifier`)}
                                                                    autoComplete="off"
                                                                    required
                                                                />
                                                                {formik.touched.banners?.[index]?.bannerIdentifier && formik.errors.banners?.[index]?.bannerIdentifier ? (
                                                                    <div className="error-text">{formik.errors.banners[index].bannerIdentifier}</div>
                                                                ) : null}
                                                                <hr className={formik.touched.banners?.[index]?.bannerIdentifier && formik.errors.banners?.[index]?.bannerIdentifier ? "error-hr-line" : ""} />

                                                            </div>
                                                            <div className="custom-form-group">
                                                                <label htmlFor={`banners.${index}.link`}>Add Link</label>
                                                                <input
                                                                    type="text"
                                                                    id={`banners.${index}.link`}
                                                                    {...formik.getFieldProps(`banners.${index}.link`)}
                                                                    autoComplete="off"
                                                                />
                                                                {formik.touched.banners?.[index]?.link && formik.errors.banners?.[index]?.link ? (
                                                                    <div className="error-text">{formik.errors.banners[index].link}</div>
                                                                ) : null}
                                                                <hr className={formik.touched.banners?.[index]?.link && formik.errors.banners?.[index]?.link ? "error-hr-line" : ""} />

                                                            </div>
                                                        </div>

                                                        {/* Full-width Overlay */}
                                                        {deletedBanners.includes(index) && (
                                                            <div
                                                                style={{
                                                                    position: "absolute",
                                                                    top: 0,
                                                                    left: 0,
                                                                    width: "100%",
                                                                    height: "100%",
                                                                    background: "rgba(0, 0, 0, 0.65)",
                                                                    display: "flex",
                                                                    justifyContent: "center",
                                                                    alignItems: "center",
                                                                    zIndex: 5,
                                                                }}
                                                            >

                                                                <button
                                                                    onClick={() => handleUndoDelete(index)}
                                                                    className="simpleBtn"
                                                                    type="button"
                                                                >
                                                                    <span>UNDO DELETE</span>
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>

                <div className="form-card">
                    <div
                        className="file-upload"
                        style={{
                            height: "100%",
                            border: isDragging ? "2px dashed rgb(74, 143, 226)" : "1px solid rgb(151, 151, 151)",
                            padding: "18px 40px",
                            cursor: "pointer",
                            fontWeight: 500,
                            textAlign: "center",
                            color: "rgb(74, 143, 226)",
                            backgroundColor: isDragging ? "rgba(74, 143, 226, 0.1)" : "transparent",
                            transition: "all 0.3s ease"
                        }}
                        onClick={() => !enableAds ? showAlert() : triggerFileInput()}
                        onDragOver={!enableAds ? showAlert : handleDragOver}
                        onDragLeave={!enableAds ? showAlert : handleDragLeave}
                        onDrop={!enableAds ? showAlert : handleDrop}
                    >
                        <input
                            type="file"
                            id="fileInput"
                            accept="image/png"
                            style={{ display: "none" }}
                            onChange={handleFileChange}
                        />
                        {isDragging ? "Drop image here" : "+ Add Banner graphic. Drop Image"}
                    </div>
                </div>

                <div className="text-right normal_btn" style={{
                    width:"560px"
                }}>
                    <div className="button-div ">
                        <button type="submit" className="text-white" disabled={formik.isSubmitting || !enableAds }>
                            {formik.isSubmitting ? "Saving..." : "Save"}
                        </button>
                    </div>
                </div>
            </form>

            <CropperModal show={showModal} onClose={() => setShowModal(false)} image={image} onCrop={handleCrop} />
        </div>
    );
};

export default BannerAd;